import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDatabase, FiExternalLink, FiCheck, FiX, FiAlertTriangle, FiWifi, FiWifiOff, FiRefreshCw, FiShield } = FiIcons;

const DataSources = ({ sources }) => {
  const defaultSources = [
    { 
      name: 'PAGASA (Primary Source)', 
      url: 'https://www.pagasa.dost.gov.ph', 
      status: 'primary', 
      description: 'Official Philippine weather authority providing the base data for all forecasts, typhoon signals, and alerts on this platform.',
      isOfficial: true
    },
    { 
      name: 'JTWC (Secondary Monitoring)', 
      url: 'https://www.metoc.navy.mil/jtwc', 
      status: 'fallback', 
      description: 'Used for cross-referencing typhoon tracks entering the Mindanao Sea.' 
    },
    { 
      name: 'Windy API', 
      url: 'https://api.windy.com', 
      status: 'supplement', 
      description: 'Provides high-resolution visual wind and rain patterns for Northern Mindanao.' 
    }
  ];

  const sourcesToShow = sources || defaultSources;

  const getStatusColor = (status) => {
    switch (status) {
      case 'primary': return 'text-green-600 bg-green-100 border-green-200';
      case 'monitoring': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'fallback': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: 0.4 }} 
      className="bg-white rounded-xl shadow-lg p-6 border border-blue-100" 
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="p-2 bg-blue-100 rounded-lg mr-3">
            <SafeIcon icon={FiDatabase} className="text-xl text-blue-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Official Data Pipeline</h2>
        </div>
        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">SYNC ACTIVE</span>
      </div>

      <div className="space-y-4">
        {sourcesToShow.map((source, index) => (
          <motion.div 
            key={source.name} 
            initial={{ opacity: 0, x: -10 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ delay: 0.1 * index }}
            className={`p-4 rounded-xl border transition-all ${source.isOfficial ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-100 shadow-sm' : 'bg-gray-50 border-gray-100'}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className={`font-bold text-sm ${source.isOfficial ? 'text-blue-900' : 'text-gray-900'}`}>
                    {source.name}
                  </h3>
                  {source.isOfficial && (
                    <SafeIcon icon={FiShield} className="text-blue-600 text-xs" />
                  )}
                  <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold uppercase ${getStatusColor(source.status)}`}>
                    {source.status}
                  </span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {source.description}
                </p>
              </div>
              {source.url && (
                <a href={source.url} target="_blank" rel="noopener noreferrer" className="ml-3 p-2 bg-white rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 transition-colors">
                  <SafeIcon icon={FiExternalLink} className="text-sm" />
                </a>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-slate-900 text-white rounded-xl">
        <div className="flex items-center mb-2">
          <SafeIcon icon={FiShield} className="text-blue-400 mr-2" />
          <span className="text-xs font-bold uppercase tracking-widest">PAGASA Compliance</span>
        </div>
        <p className="text-[11px] text-slate-300 leading-relaxed">
          This system adheres to the Open Data policies of the Department of Science and Technology (DOST). 
          All meteorological data is processed in accordance with PAGASA's data distribution protocols.
        </p>
      </div>
    </motion.div>
  );
};

export default DataSources;